import { subscribeTestsStore, TESTS_SPEED_OPTIONS, useTestsStore } from '@lved/ui/store/testsStore';
import { Command, CommandRegistry, MenuContribution, MenuModelRegistry } from '@theia/core';
import {
    AbstractViewContribution,
    codicon,
    CommonMenus,
    FrontendApplication,
    FrontendApplicationContribution
} from '@theia/core/lib/browser';
import { TabBarToolbarContribution, TabBarToolbarRegistry } from '@theia/core/lib/browser/shell/tab-bar-toolbar';
import { BOTTOM_AREA_ID } from '@theia/core/lib/browser/shell/theia-dock-panel';
import { Widget } from '@theia/core/lib/browser/widgets';
import { SelectComponent, SelectOption } from '@theia/core/lib/browser/widgets/select-component';
import { injectable } from '@theia/core/shared/inversify';
import * as React from '@theia/core/shared/react';
import { TestsPanel } from './tests-panel';

const TestsCommand: Command = { id: 'lvgl-tests:open', label: 'Tests: Open' };
const TestsRunCommand: Command = { id: 'lvgl-tests:run', label: 'Run Test' };
const TestsRunAllCommand: Command = {
    id: 'lvgl-tests:run-all',
    label: 'Run All Tests',
    iconClass: codicon('run-all')
};
const TESTS_SPEED_SELECT_ID: string = 'lvgl-tests-speed-select';

@injectable()
export class TestsPanelContribution
    extends AbstractViewContribution<TestsPanel>
    implements FrontendApplicationContribution, MenuContribution, TabBarToolbarContribution
{
    async onDidInitializeLayout(_app: FrontendApplication): Promise<void> {
        await this.openView({ activate: false, reveal: false });
    }

    constructor() {
        super({
            widgetId: TestsPanel.ID,
            widgetName: TestsPanel.LABEL,
            defaultWidgetOptions: { area: 'bottom' },
            toggleCommandId: TestsCommand.id
        });
    }

    override registerCommands(commands: CommandRegistry): void {
        commands.registerCommand(TestsCommand, {
            execute: async () => {
                const widget: TestsPanel | undefined = await this.widgetManager.getOrCreateWidget<TestsPanel>(
                    TestsPanel.ID
                );
                await this.shell.addWidget(widget, { area: 'bottom' });
                await this.shell.activateWidget(widget.id);
            }
        });

        const isTestsToolbarWidget = (widget?: Widget): boolean =>
            widget instanceof Widget && widget.id === TestsPanel.ID && widget.parent?.id === BOTTOM_AREA_ID;

        const runTests = async (): Promise<void> => {
            const widget: TestsPanel | undefined = this.widgetManager.tryGetWidget<TestsPanel>(TestsPanel.ID);
            widget?.runTest();
        };

        commands.registerCommand(TestsRunCommand, {
            execute: runTests,
            isEnabled: () => this.canExecuteTestsCommand('single'),
            isVisible: (widget) => isTestsToolbarWidget(widget) && useTestsStore.getState().mode === 'single'
        });

        commands.registerCommand(TestsRunAllCommand, {
            execute: runTests,
            isEnabled: () => this.canExecuteTestsCommand('project'),
            isVisible: (widget) => isTestsToolbarWidget(widget) && useTestsStore.getState().mode === 'project'
        });
    }

    override registerMenus(menus: MenuModelRegistry): void {
        menus.registerMenuAction(CommonMenus.VIEW_VIEWS, {
            commandId: TestsCommand.id,
            label: TestsPanel.LABEL
        });
    }

    registerToolbarItems(toolbar: TabBarToolbarRegistry): void {
        const onDidChange = subscribeTestsStore;
        const isTestsToolbarWidget = (widget?: Widget): boolean =>
            widget instanceof Widget && widget.id === TestsPanel.ID && widget.parent?.id === BOTTOM_AREA_ID;

        toolbar.registerItem({
            id: 'lvgl-tests:run',
            command: TestsRunCommand.id,
            render: () => this.renderRunToolbarItem(),
            isVisible: (widget) => isTestsToolbarWidget(widget) && useTestsStore.getState().mode === 'single',
            onDidChange,
            priority: 0
        });

        toolbar.registerItem({
            id: 'lvgl-tests:run-all',
            command: TestsRunAllCommand.id,
            render: () => this.renderRunAllToolbarItem(),
            isVisible: (widget) => isTestsToolbarWidget(widget) && useTestsStore.getState().mode === 'project',
            onDidChange,
            priority: 0
        });

        toolbar.registerItem({
            id: 'lvgl-tests:speed',
            render: () => this.renderSpeedSelector(),
            isVisible: (widget) => isTestsToolbarWidget(widget) && useTestsStore.getState().mode === 'single',
            onDidChange,
            priority: 1
        });
    }

    protected renderRunAllToolbarItem(): React.ReactNode {
        const enabled: boolean = this.canExecuteTestsCommand('project');
        const isRunning: boolean = useTestsStore.getState().isRunning;

        return React.createElement('div', {
            id: TestsRunAllCommand.id,
            key: TestsRunAllCommand.id,
            className: codicon(isRunning ? 'debug-stop' : 'run-all', true),
            title: isRunning ? 'Stop Tests' : TestsRunAllCommand.label,
            onClick: (event: React.MouseEvent<HTMLDivElement>) => {
                event.preventDefault();
                event.stopPropagation();
                if (enabled) this.getTestsPanel()?.runTest();
            }
        });
    }

    protected renderRunToolbarItem(): React.ReactNode {
        const enabled: boolean = this.canExecuteTestsCommand('single');
        const isRunning: boolean = useTestsStore.getState().isRunning;

        return React.createElement('div', {
            id: TestsRunCommand.id,
            key: TestsRunCommand.id,
            className: codicon(isRunning ? 'debug-stop' : 'play', true),
            title: isRunning ? 'Stop Test' : TestsRunCommand.label,
            onClick: (event: React.MouseEvent<HTMLDivElement>) => {
                event.preventDefault();
                event.stopPropagation();
                if (enabled) this.getTestsPanel()?.runTest();
            }
        });
    }

    protected renderSpeedSelector(): React.ReactNode {
        const { speed } = useTestsStore.getState();
        const options: SelectOption[] = TESTS_SPEED_OPTIONS.map((option) => ({
            value: String(option.value),
            label: option.label
        }));

        return React.createElement(
            'div',
            { id: TESTS_SPEED_SELECT_ID, key: TESTS_SPEED_SELECT_ID },
            React.createElement(SelectComponent, {
                key: String(speed.value),
                options,
                defaultValue: String(speed.value),
                onChange: (option) => {
                    const value = Number(option.value);
                    if (Number.isNaN(value)) return;
                    this.widgetManager.tryGetWidget<TestsPanel>(TestsPanel.ID)?.setSpeed(value);
                }
            })
        );
    }

    private getTestsPanel(): TestsPanel | undefined {
        return this.widgetManager.tryGetWidget<TestsPanel>(TestsPanel.ID);
    }

    private canExecuteTestsCommand(mode: 'single' | 'project'): boolean {
        const { mode: currentMode, canRun, isRunning } = useTestsStore.getState();
        return currentMode === mode && (isRunning || canRun);
    }
}
